import {
  Box,
  Button,
  InputBase,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CustomTable from "../../components/common-components/CustomTable";
import {
  AddIcon,
  DeleteIconTable,
  EditIconTable,
} from "../../assets/Icon/ArrowIcon";
import SearchIcon from "@mui/icons-material/Search";
import AccountStatusButton from "../../components/common-components/AccountStatusButton";

import NoDataFound from "../../components/common-components/NoDataFound";
import AddAgentModal from "./AddAgents";
import DeleteModal from "./DeleteAgents";
import { useAppDispatch } from "../../stores/store";
import { useSelector } from "react-redux";
import { getAgentList } from "../../stores/agents";
import { setToast } from "../../stores/toast/toastslice";
import CommonPagination from "../../components/common-components/CommonPagination";
import ListLoadingNew from "../../components/common-components/ListLoadingNew";

const Agents = () => {
  const dispatch = useAppDispatch();
  const {
    data: agentList,
    status,
    error,
    total_records,
  } = useSelector((state: any) => state.agents);

  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);

  const [page, setPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState<any>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    fetchAgents();
  }, [page, limit, debouncedSearch]);

  const fetchAgents = () => {
    const offset = (page - 1) * limit;

    dispatch<any>(
      getAgentList({
        role_id: 3,
        offset,
        limit,
        search: searchQuery,
      })
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setCurrentPage(newPage); // Update the UI page state
  };

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => {
    setOpenModal(false);
    setSelectedAgentId(null);
  };

  const handleOpenDeleteModal = (agentId: number) => {
    setSelectedAgentId(agentId);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedAgentId(null);
  };

  const handleOpenEdit = (agentId: number) => {
    setSelectedAgentId(agentId);
    setOpenModal(true);
  };

  return (
    <StyledBox>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          padding: "20px",
          "@media (min-width: 600px)": {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          },
        }}
      >
        <Title>Agents</Title>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            "@media (min-width: 600px)": {
              flexDirection: "row",
              alignItems: "center",
            },
          }}
        >
          <SearchWrapper>
            <SearchIcon />
            <StyledInputBase
              placeholder="Search here..."
              inputProps={{ "aria-label": "search" }}
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </SearchWrapper>

          <StyledButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
          >
            Add Agent
          </StyledButton>
        </Box>
      </Box>

      {status === "loading" ? (
        // <Box
        //   display="flex"
        //   justifyContent="center"
        //   alignItems="center"
        //   height="50vh"
        // >
        //   <CircularProgress />
        // </Box>
        <ListLoadingNew />
      ) : agentList?.length > 0 ? (
        <>
          <CustomTable
            headings={[
              "Sr. No",
              "First Name",
              "Last Name",
              "Email",
              "Account Status",
            ]}
            isAgentTable
          >
            {agentList?.map((item: any, index: number) => (
              <TableRow key={item?.id || index}>
                <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                <TableCell>{item?.first_name || "-"}</TableCell>
                <TableCell>{item?.last_name || "-"}</TableCell>
                <TableCell>{item?.email || "-"}</TableCell>
                <TableCell
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <AccountStatusButton
                    rowstatus={item?.is_active ?? false}
                    agentId={item?.id ?? 0}
                  />

                  <Box sx={{ display: "flex", gap: "10px" }}>
                    {item?.id && (
                      <>
                        <Box
                          onClick={() => handleOpenDeleteModal(item.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <DeleteIconTable />
                        </Box>
                        <Box
                          onClick={() => handleOpenEdit(item.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <EditIconTable />
                        </Box>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </CustomTable>

          <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
            <CommonPagination
              currentPage={page}
              totalPages={Math.ceil(total_records / limit)}
              onPageChange={handlePageChange}
            />
          </Box>
        </>
      ) : (
        <NoDataFound
          title="No Agents Found"
          subTitle="Try adding new agents or modifying your search criteria."
          status={true}
        />
      )}

      <AddAgentModal
        open={openModal}
        handleClose={handleClose}
        agentId={selectedAgentId}
        onSuccess={fetchAgents}
      />

      <DeleteModal
        open={openDeleteModal}
        handleClose={handleCloseDeleteModal}
        id={selectedAgentId}
        onSuccess={fetchAgents}
      />
    </StyledBox>
  );
};

export default Agents;

const StyledBox = styled(Box)({
  background: "#ffffff",
  boxShadow: "0px 2px 40px 0px #DDE3EA33",
  borderRadius: "10px",
  padding: "16px",
  margin: "16px",
});

const Container = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "20px",
});

const Title = styled(Typography)({
  color: "#01060B !important",
  fontSize: "20px !important",
  fontWeight: "600 !important",
});

const SearchContainer = styled(Box)({
  display: "flex",
  gap: "10px",
  alignItems: "center",
});

const SearchWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  background: "#F6F9FD",
  color: "#6B6B6B",
  borderRadius: "8px",
  padding: "5px 10px",
  "&:hover": {
    background: "#F6F9FD",
  },
});

const StyledButton = styled(Button)({
  width: "120px !important",
  whiteSpace: "nowrap",
  borderRadius: "6px !important",
  height: "40px !important",
});

const StyledInputBase = styled(InputBase)({
  minWidth: "300px",
  padding: "3px",
});
